import { HttpErrorResponse, HttpHeaders, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, tap, throwError } from 'rxjs';
import { isApiRequest, isSignInRequest } from './api-origin';
import { SessionStore } from './session.store';

/** Response header the API uses to hand back a fresh token after each signed-in call; CORS exposes it. */
export const RENEWED_TOKEN_HEADER = 'renewed-token';

/**
 * Attaches the token to API requests only, never to sign-in (a failed sign-in would be logged against
 * that token's rep). A sign-in 401 is a wrong password and passes through; any other API 401 ends the
 * session as expired. A 503 or network failure never ends it. A `renewed-token` header on a success or
 * a non-401 error replaces the token this request carried, which keeps an active rep signed in.
 */
export const authInterceptor: HttpInterceptorFn = (request, next) => {
  const session = inject(SessionStore);

  const ours = isApiRequest(request.url);
  const signIn = ours && isSignInRequest(request.url);
  const token = session.accessToken();
  const sentToken = token !== null && ours && !signIn ? token : null;

  const outbound =
    sentToken !== null
      ? request.clone({ setHeaders: { Authorization: `Bearer ${sentToken}` } })
      : request;

  // renew() refuses the token when the session changed since this request left, so within this tab the
  // first of two concurrent answers wins; the other token is valid too.
  const applyRenewal = (headers: HttpHeaders): void => {
    const renewed = headers.get(RENEWED_TOKEN_HEADER);

    if (sentToken !== null && renewed !== null && renewed !== '') {
      session.renew(renewed, sentToken);
    }
  };

  return next(outbound).pipe(
    tap({
      next: (event) => {
        if (event instanceof HttpResponse) {
          applyRenewal(event.headers);
        }
      },
      error: (error: unknown) => {
        if (error instanceof HttpErrorResponse && error.status !== 401) {
          applyRenewal(error.headers);
        }
      },
    }),
    catchError((error: unknown) => {
      // Only the token this request carried can have expired: a late 401 for an older token must
      // not end the current session.
      if (
        error instanceof HttpErrorResponse &&
        error.status === 401 &&
        sentToken !== null &&
        session.accessToken() === sentToken
      ) {
        session.clear('expired');
      }

      return throwError(() => error);
    }),
  );
};
